import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Users, Check, AlertCircle, ExternalLink, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Billing / paywall settings for club ADMIN. The whole page is safe to
// visit whether or not the paywall is switched on — when it's off, we
// show a "Coming Soon" banner and no upgrade buttons. When it's on,
// the current plan card, usage stats, and upgrade/manage buttons appear.
//
// Success return path from Stripe: /billing?session_id=cs_xxx — we poll
// the backend to confirm the payment landed and toast on success.
export default function Billing() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to load billing status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Handle Stripe redirect back to /billing?session_id=... — poll the
  // backend until Stripe confirms the payment landed, then refresh
  // status and clear the query string so a refresh doesn't re-poll.
  useEffect(() => {
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info("Checkout was cancelled — no charge made.");
      setParams({}, { replace: true });
      return;
    }
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) {
            toast.success("Subscription active — welcome aboard!");
            setParams({}, { replace: true });
            load();
          }
          return;
        }
        if (data.status === "expired") {
          if (!cancelled) {
            toast.error("Checkout session expired.");
            setParams({}, { replace: true });
          }
          return;
        }
      } catch (ex) {
        // 404 during first millisecond after Stripe redirect is
        // possible — keep polling a few times before giving up.
      }
      if (attempts < 8 && !cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [params.get("session_id"), params.get("canceled")]);

  if (loading || !status) {
    return (
      <div className="p-4 md:p-8 max-w-4xl" data-testid="billing-page-loading">
        <Loader2 className="animate-spin muted-text" />
      </div>
    );
  }

  const {
    paywall_enabled,
    plan_tier,
    photographer_limit,
    photographer_count,
    plan_expires_at,
    grace_ends_at,
    subscription_status,
    is_locked,
    is_soft_locked,
    days_remaining,
    plans_config,
  } = status;

  const upgrade = async (tier) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to start checkout");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to open billing portal");
      setBusy(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl space-y-8" data-testid="billing-page">
      <header>
        <div className="ccs-label mb-2">Administration</div>
        <h1 className="text-4xl font-semibold tracking-tight">Billing</h1>
        <p className="muted-text mt-2 text-sm">
          Manage your Camera Club Score subscription, plan, and billing details.
        </p>
      </header>

      {!paywall_enabled && (
        <div
          className="ccs-card border-amber-800/50 bg-amber-900/10"
          data-testid="billing-dormant-notice"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-400 shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <div className="text-amber-300 font-medium mb-1">Billing preview</div>
              <div className="muted-text">
                Subscriptions are not currently active on Camera Club Score.
                This page shows what will be available once billing launches — no
                charges will be made and no plan changes will apply. Existing
                clubs will remain free.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current plan summary */}
      <section data-testid="billing-current-plan">
        <div className="ccs-label mb-2">Your plan</div>
        <div className="ccs-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-2xl font-semibold" data-testid="current-plan-name">
                {planLabel(plan_tier)}
              </div>
              {subscription_status && (
                <div className="muted-text text-xs mt-1">
                  Subscription status:{" "}
                  <span className="mono uppercase">{subscription_status}</span>
                </div>
              )}
              {plan_expires_at && plan_tier !== "grandfathered" && (
                <div className="muted-text text-xs mt-1">
                  {plan_tier === "trial" ? "Trial ends" : "Renews"}{" "}
                  {fmt(plan_expires_at)}
                  {days_remaining != null && (
                    <> · <span className="text-white">{days_remaining} day{days_remaining === 1 ? "" : "s"} remaining</span></>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="ccs-label">Photographers</div>
                <div className="mono text-2xl font-semibold" data-testid="photographer-count">
                  {photographer_count}
                  {photographer_limit != null && (
                    <span className="muted-text text-base"> / {photographer_limit}</span>
                  )}
                </div>
              </div>
              <Users className="text-amber-400" size={28} />
            </div>
          </div>

          {is_locked && (
            <div
              className="mt-4 text-sm bg-rose-900/20 border border-rose-800/60 px-3 py-2"
              data-testid="billing-locked-banner"
            >
              Your plan has expired. Renewing will unlock all features.
            </div>
          )}
          {is_soft_locked && !is_locked && (
            <div
              className="mt-4 text-sm bg-amber-900/20 border border-amber-800/60 px-3 py-2"
              data-testid="billing-grace-banner"
            >
              Your plan is in a 14-day grace period. Please renew within{" "}
              {days_remaining ?? 0} day(s) to avoid interruption.
            </div>
          )}
        </div>
      </section>

      {/* Plan tiles */}
      <section data-testid="billing-plans">
        <div className="ccs-label mb-2">Available plans</div>
        <div className="grid md:grid-cols-2 gap-4">
          {Object.entries(plans_config || {}).map(([tier, cfg]) => {
            const isCurrent = plan_tier === tier;
            const canUpgrade = paywall_enabled && !isCurrent && user?.role === "ADMIN";
            return (
              <div
                key={tier}
                className={
                  "ccs-card " +
                  (isCurrent ? "border-amber-400/50" : "")
                }
                data-testid={`plan-card-${tier}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="ccs-label">{cfg.name}</div>
                    <div className="text-3xl font-semibold mt-1">
                      £{cfg.price_gbp}
                      <span className="muted-text text-base">/year</span>
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="mono text-[10px] uppercase text-amber-400 border border-amber-800 px-2 py-0.5">
                      Current
                    </span>
                  )}
                </div>
                <ul className="text-sm muted-text space-y-1.5 mb-4">
                  <li className="flex items-center gap-2">
                    <Check size={14} className="text-emerald-400 shrink-0" />
                    {cfg.photographer_limit
                      ? `Up to ${cfg.photographer_limit} photographers`
                      : "Unlimited photographers"}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={14} className="text-emerald-400 shrink-0" />
                    All competitions, seasons & stats
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={14} className="text-emerald-400 shrink-0" />
                    Public portal, exports, PDF generation
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={14} className="text-emerald-400 shrink-0" />
                    Auto-renewing annual billing (cancel anytime)
                  </li>
                </ul>
                <button
                  onClick={() => upgrade(tier)}
                  disabled={!canUpgrade || busy}
                  className="ccs-btn w-full flex items-center justify-center gap-2"
                  data-testid={`upgrade-${tier}-button`}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                  {isCurrent
                    ? "Current plan"
                    : paywall_enabled
                      ? `Upgrade to ${cfg.name}`
                      : "Coming soon"}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Manage subscription (only when they have one) */}
      {paywall_enabled && subscription_status && (
        <section data-testid="billing-manage">
          <div className="ccs-label mb-2">Manage</div>
          <button
            onClick={openPortal}
            disabled={busy}
            className="ccs-btn-ghost text-sm flex items-center gap-2"
            data-testid="open-billing-portal"
          >
            <ExternalLink size={14} /> Open Stripe billing portal
          </button>
          <p className="muted-text text-xs mt-2">
            Update your card, download invoices, or cancel your subscription.
          </p>
        </section>
      )}
    </div>
  );
}


function planLabel(tier) {
  return {
    grandfathered: "Free (early adopter)",
    trial: "30-day free trial",
    starter: "Starter",
    unlimited: "Unlimited",
    expired: "Expired",
  }[tier] || tier;
}


function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  } catch (_) {
    return iso;
  }
}
